import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'react';
import {
  TextField,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/pro-light-svg-icons';
import { debounce } from 'lodash';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { StyledAccordion } from './CenterContainer';
import { SelectorOptions } from 'shared/design-system/components/Selector';

export const replaceBeneficiary = (name: string, term?: string) => {
  if (!term) return name;
  let result = name;
  result = result.replace(/People/g, term[0].toUpperCase() + term.slice(1));
  result = term.includes('people')
    ? result
    : result.replace(/people/g, term[0].toUpperCase() + term.slice(1));
  return result;
};

export interface Indicator {
  id: number;
  name: string;
  stageId?: number;
  definition?: string;
}

export interface SelectedIndicators {
  [x: string]: Indicator | undefined;
}

export interface IndicatorGroups {
  [x: string]: Indicator[];
}

interface IndicatorProps {
  group: string;
  values: Indicator[];
  searchText: string;
  onlyShowStageId?: number;
  beneficiaryTerm?: string;
  selected: SelectedIndicators;
  handleChange: (value: Indicator) => unknown;
  setEmpty: (empty: boolean) => unknown;
}

const getIndicatorLabel = (term?: string) => (i: Indicator): string =>
  replaceBeneficiary(i.name, term);

const Indicator: React.FC<IndicatorProps> = ({
  group,
  selected,
  values,
  beneficiaryTerm,
  handleChange,
  searchText,
  setEmpty,
}) => {
  const [expanded, setExpanded] = useState<boolean>(searchText.length >= 3);
  const [options, setOptions] = useState<Indicator[]>(values);

  useEffect(() => {
    if (searchText.length < 3) {
      setOptions(values);
      setEmpty(false);
      setExpanded(false);
      return;
    }
    setExpanded(true);
    setOptions(o => {
      const text = searchText.toLowerCase();
      const isBeneficiaryTerm = beneficiaryTerm
        ? text.includes(beneficiaryTerm.toLowerCase())
        : false;
      const newState = values.filter(
        v =>
          v.name.toLowerCase().includes(text) ||
          (isBeneficiaryTerm && v.name.toLowerCase().includes('people')),
      );
      if (newState.length > 0) setEmpty(false);
      return newState;
    });
  }, [searchText, setOptions]);

  const handleToggle = useCallback(() => {
    setExpanded(o => !o);
  }, [setExpanded]);

  if (options.length === 0) return null;

  return (
    <StyledAccordion expanded={expanded}>
      <AccordionSummary
        expandIcon={<FontAwesomeIcon icon={faAngleDown} />}
        aria-controls="panel1a-content"
        id="panel1a-header"
        onClick={handleToggle}
      >
        <TrueImpactText className="group-name" variant="heading-subheading">
          {group}
        </TrueImpactText>
      </AccordionSummary>
      <AccordionDetails className="accordion-details">
        <SelectorOptions
          variant="checkbox"
          options={options}
          getLabel={getIndicatorLabel(beneficiaryTerm)}
          getSelected={val => Boolean(selected[val.name])}
          onClick={handleChange}
          tooltip={val=> val.definition || ''}
        />
      </AccordionDetails>
    </StyledAccordion>
  );
};

interface GroupsProps {
  onlyShowStageId?: number;
  selected: SelectedIndicators;
  indicators: IndicatorGroups;
  beneficiaryTerm?: string;
  handleChange: (indicator: Indicator) => unknown;
  searchText: string;
  setEmpty: (empty: boolean) => unknown;
}

const Groups: React.FC<GroupsProps> = React.memo(
  ({
    indicators,
    selected,
    beneficiaryTerm,
    handleChange,
    searchText,
    onlyShowStageId,
    setEmpty,
  }) => {
    setEmpty(true);
    return (
      <div className="container">
        {Object.keys(indicators).map(group => {
          const i =
            onlyShowStageId === undefined
              ? indicators[group]
              : indicators[group].filter(x => x.stageId === onlyShowStageId);
          return (
            <Indicator
              selected={selected}
              group={group}
              values={i}
              key={group}
              beneficiaryTerm={beneficiaryTerm}
              searchText={searchText}
              setEmpty={setEmpty}
              onlyShowStageId={onlyShowStageId}
              handleChange={handleChange}
            />
          );
        })}
      </div>
    );
  },
);

interface GroupedIndicatorsProps {
  onlyShowStageId?: number;
  selected: SelectedIndicators;
  indicators: IndicatorGroups;
  beneficiaryTerm?: string;
  handleChange: (indicator: Indicator) => unknown;
}

export const GroupedIndicators: React.FC<GroupedIndicatorsProps> = props => {
  const ref = useRef<{ value: string }>();
  const [search, setSearch] = useState('');
  const [isEmpty, setEmpty] = useState(true);
  const throttled = useMemo(
    () =>
      debounce(() => {
        if (!ref.current) return;
        setSearch(ref.current.value.toLowerCase());
      }, 1000),
    [],
  );
  useEffect(() => {
    return () => {
      throttled.cancel();
    };
  }, []);

  return (
    <>
      <TextField
        inputRef={ref}
        fullWidth
        // value={text}
        label="Search"
        onChange={throttled}
        variant="outlined"
      />
      <Groups
        {...props}
        searchText={search.toLowerCase()}
        setEmpty={setEmpty}
      />
      {isEmpty && (
        <p>
          Your search doesn't match any indicators, try something less specific.
        </p>
      )}
    </>
  );
};
